/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { useResize, animated, useSpring } from '@react-spring/web';
import { ChevronRight } from 'lucide-react';
import { FC, PropsWithChildren, ReactNode, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

type CollapsibleSectionProps = {
    title: ReactNode;
    subtitle?: string;
    defaultOpen?: boolean;
    containerClassName?: string;
    headerClassName?: string;
    theme?: 'small' | 'medium';
};

export const CollapsibleSection: FC<PropsWithChildren<CollapsibleSectionProps>> = ({
    title,
    subtitle,
    defaultOpen,
    children,
    containerClassName,
    headerClassName,
    theme = 'medium',
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen ?? false);
    const content = useRef<HTMLDivElement>(null);

    const { height } = useResize({ container: content });

    const spring = useSpring({
        from: { rotate: '0deg' },
        to: theme === 'small' ? { rotate: isOpen ? '-90deg' : '90deg' } : { rotate: isOpen ? '90deg' : '0deg' },
    });
    const heightSpring = useSpring({
        to: { height: isOpen ? height : 0 },
    });

    let chevronDimensions;

    switch (theme) {
        case 'small':
            chevronDimensions = { size: '16px' };
            break;
        case 'medium':
        default:
            chevronDimensions = { size: '24px' };
            break;
    }

    return (
        <div className={cn('py-2 max-w-full', containerClassName)}>
            <div
                role="button"
                aria-label="Open the step options"
                className={cn(
                    'flex items-center gap-x-1 cursor-pointer',
                    theme === 'small' && 'text-slate-500',
                    headerClassName,
                )}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
            >
                <animated.div style={{ ...spring }}>
                    <ChevronRight {...chevronDimensions} />
                </animated.div>
                <span
                    className={cn(
                        '',
                        theme === 'medium' && 'text-lg font-medium',
                        theme === 'small' && '-order-1 text-sm',
                    )}
                >
                    {title}
                </span>
                {subtitle && <span className="text-sm">{subtitle}</span>}
            </div>
            <div className={cn('expandToAutoHeight', isOpen ? '' : 'collapsed')}>
                <div className="min-h-0 overflow-hidden" {...(isOpen ? {} : { inert: true })} ref={content}>
                    {children}
                </div>
            </div>
        </div>
    );
};
